import { SensorOccupied as SensorOccupiedIcon } from "@mui/icons-material";
import { Chip } from "@mui/material";
import { startCase } from "lodash";
import { FC } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { IAdditionEntity, ICustomField, SINGLE_KEY } from "@ryde/shared";

import { ColorIconButton } from "../../../controls";
import { MultiselectTemplate } from "../multiselect.template";
import { NumberTemplate } from "../number.template";
import { PassengerAdditionReasonRow } from "./passenger-addition.reason-row";
import {
  PassengerAdditionFormStyled,
  PassengerAdditionWrapperStyled,
} from "./passenger-addition.styled";
import { PassengerAdditionTierRow } from "./passenger-addition.tier-row";
import { usePassengerAddition } from "./use-passenger-addition";

export const PassengerAdditions: FC<ICustomField> = ({ name }) => {
  const { t } = useTranslation();

  const {
    tiers,
    draftMethods,
    handleAddTier,
    handleEditTier,
    handleClearTier,
    handleClearTierForm,
    specificReasons,
    groupedEntities,
    dropdownItems,
    handleAddSpecificReason,
    handleEditSpecificReason,
    handleClearSpecificReason,
    handleClearSpecificReasonForm,
  } = usePassengerAddition(name);

  return (
    <PassengerAdditionWrapperStyled>
      <Chip
        label={startCase(t(`passenger.${SINGLE_KEY}`))}
        icon={<SensorOccupiedIcon />}
      />

      <p className="section-title">
        {t("priceRate.formLabel.passengersPricing.0")}
      </p>

      <FormProvider {...draftMethods}>
        {tiers.map(tier => (
          <PassengerAdditionTierRow
            key={tier.from}
            from={tier.from}
            price={tier.price}
            onEdit={() => handleEditTier(tier.from, tier.price)}
            onClear={() => handleClearTier(tier.from)}
          />
        ))}

        <PassengerAdditionFormStyled>
          <div className="form-fields">
            <NumberTemplate
              labelTranslated={t(
                "priceRate.formLabel.passengersPricing.fromPassenger",
              )}
              name="tierFrom"
            />
            <NumberTemplate
              labelTranslated={t(
                "priceRate.formLabel.passengersPricing.pricePerPassenger",
              )}
              name="tierPrice"
            />
          </div>
          <div className="form-buttons">
            <ColorIconButton iconType="CHECK" onClick={handleAddTier} />
            <ColorIconButton iconType="CANCEL" onClick={handleClearTierForm} />
          </div>
        </PassengerAdditionFormStyled>

        {!!specificReasons?.length &&
          groupedEntities.map((groupedReasons: IAdditionEntity[]) => (
            <PassengerAdditionReasonRow
              key={groupedReasons[0].id}
              name={groupedReasons.map(reason => reason.name).join(", ")}
              price={groupedReasons[0].price}
              onEdit={() =>
                handleEditSpecificReason(
                  groupedReasons?.map(reason => reason.id) || [],
                  groupedReasons[0].price,
                )
              }
              onClear={() => handleClearSpecificReason(groupedReasons[0].price)}
            />
          ))}

        <PassengerAdditionFormStyled>
          <div className="form-fields">
            <MultiselectTemplate
              labelTranslated={t("passenger.PASSENGERS_WITH_SPECIFIC_REASONS")}
              formAttributes={{ selectItems: dropdownItems }}
              name="reasonsExtraSelect"
            />
            <NumberTemplate
              labelTranslated={t(
                "passenger.PRICE_FOR_PASSENGER_WITH_SPECIFIC_REASON",
              )}
              name="extraPrice"
            />
          </div>
          <div className="form-buttons">
            <ColorIconButton
              iconType="CHECK"
              onClick={handleAddSpecificReason}
            />
            <ColorIconButton
              iconType="CANCEL"
              onClick={handleClearSpecificReasonForm}
            />
          </div>
        </PassengerAdditionFormStyled>
      </FormProvider>
    </PassengerAdditionWrapperStyled>
  );
};
